import React, { useState, useEffect, useRef } from 'react';
import { Switch } from "@Components/shadcn/ui/switch";
import TriggerStep from './components/TriggerStep';
import ResponseStep from './components/ResponseStep';
import { useGetFlowAutomationMutation, useUpdateFlowAutomationMutation } from '@Pages/AutomationBuilder/apiSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { displayToast } from '@Utils/toast';
import { useUpdateAutomationStatusMutation } from '@Pages/AutomationList/apiSlice';
import { validateUrl } from '@Utils/common';

interface AutomationBuilderProps {
  automationId?: string;
}

const AutomationBuilderV0: React.FC<AutomationBuilderProps> = () => {
  const [getFlowAutomation] = useGetFlowAutomationMutation();
  const [updateAutomationStatus] = useUpdateAutomationStatusMutation()
  const [updateFlowAutomation] = useUpdateFlowAutomationMutation();
  let { automationId } = useParams();

  const [triggerData, setTriggerData] = useState<any>({});
  const [responseData, setResponseData] = useState({
    autofollowData: {
      status: false,
      data: undefined
    },
    responseData: {
      type: null,
      data: {
        text: "",
        buttonText: []
      }
    }
  });
  const [automationName, setAutomationName] = useState('Untitled Automation');
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);


  // Store the original data from API
  const originalData = useRef<{
    name: string;
    trigger: any;
    response: any;
  } | null>(null);

  const navigate = useNavigate()
  const fetchAutomation = async () => {
    if (automationId) {
      try {
        const response = await getFlowAutomation({ automation_id: automationId }).unwrap();
        if (response) {
          setTriggerData(response?.data?.trigger || {});
          setResponseData(response?.data?.response || responseData);
          setAutomationName(response?.data?.name || 'Untitled Automation');
          setIsEnabled(response?.data?.status || false);
          // Store original data for comparison
          originalData.current = {
            name: response.data.name,
            trigger: response.data.trigger,
            response: response.data.response
          };

        }
      } catch (error) {
        console.error('Failed to fetch automation:', error);
        displayToast("error", 'Failed to fetch automaton!');
        navigate('/dashboard/automations');
      }
    }
  };

  useEffect(() => {
    fetchAutomation();
    document.title = "LinkPlease - Dashboard | Automation Edit";
  }, [automationId]);

  useEffect(() => {
    if (triggerData?.triggerType == "USERDM") {
      let tempResponseData = {
        ...responseData, autofollowData: {
          status: false,
          data: undefined
        }
      }
      setResponseData(tempResponseData)
    }
  }, [triggerData]);


  const hasUnsavedChanges = () => {
    if (!originalData.current) return false;
    return (
      JSON.stringify(originalData.current.name) !== JSON.stringify(automationName) ||
      JSON.stringify(originalData.current.trigger) !== JSON.stringify(triggerData) ||
      JSON.stringify(originalData.current.response) !== JSON.stringify(responseData)
    );
  };

  const CallUpdateStatusAPI = async (checked: boolean, error?: string) => {
    try {
      const response = await updateAutomationStatus({
        automation_id: automationId,
        status: checked // Pass the updated value to the API
      }).unwrap();
      if (response) {
        setIsEnabled(checked);

        if (error) {
          displayToast("error", error);
        } else {
          displayToast("success", response?.message || 'Status updated successfully');
        }

        fetchAutomation();
      }
    } catch (error) {
      console.error('Failed to update automation status:', error);
      displayToast("error", 'Failed to update automation status');
    }
  }

  const handleSwitchChange = async (checked: boolean) => {
    if (checked && hasUnsavedChanges()) {
      displayToast("warning", 'Please save your changes before enabling the automation.');
      return;
    }
    const valid_trigger = ValidateTrigger()
    const valid_response = ValidateResponse()
    if (!checked || (valid_trigger && valid_response)) {
      await CallUpdateStatusAPI(checked)
    } else {
      displayToast("warning", 'Cannot turn ON automations due to error');
    };

  };

  const handleNameEdit = () => {
    setIsNameEditing(true);
  };

  const handleNameSave = (e: React.FormEvent) => {
    e.preventDefault();
    setIsNameEditing(false);
  };

  const handleSaveAutomation = async () => {
    try {
      const automationData = {
        automation_id: automationId,
        name: automationName,
        isEnabled,
        data: {
          trigger: triggerData,
          response: responseData
        }
      };

      const valid_trigger = ValidateTrigger()
      const valid_response = ValidateResponse()
      const resp = await updateFlowAutomation(automationData).unwrap();

      if (resp) {
        displayToast("success", resp?.message);
        originalData.current = {
          name: automationName,
          trigger: triggerData,
          response: responseData
        };

        if (isEnabled && (!valid_response || !valid_trigger)) await CallUpdateStatusAPI(false, "Automation Paused due to error");
      }
    } catch (error: any) {
      displayToast("error", error?.data?.message || 'Failed to update automation status');
      console.error('Failed to save automation:', error);
    }
  };


  const ValidateTrigger = () => {
    let valid = true
    if (triggerData?.triggerType == "USERDM") {
      if (triggerData?.keywords.length == 0) {
        displayToast("error", "No Trigger Selected")
        valid = false
      }
    }

    if (triggerData?.triggerType == "POSTREELCOMMENT") {
      if (triggerData?.keywords == undefined || triggerData?.keywords.length == 0) {
        displayToast("error", "No Trigger Keyword Added")
        valid = false
      }
      if (triggerData?.selectedMedia == undefined) {
        displayToast("error", "No Trigger post/reel selected")
        valid = false
      }
      if (triggerData?.commentReplies == undefined) {
        displayToast("error", "No Trigger post/reel selected")
        valid = false
      }
    }

    if (triggerData?.triggerType == "LIVECOMMENT") {
      if (triggerData?.keywords == undefined || triggerData?.keywords.length == 0) {
        displayToast("error", "No Trigger Keyword Added")
        valid = false
      }
      if (triggerData?.selectedMedia == undefined) {
        displayToast("error", "No Trigger post/reel selected")
        valid = false
      }
    }

    return valid
  }

  const ValidateResponse = () => {
    let valid = true
    if (responseData?.responseData == undefined || responseData?.responseData?.type == "INSTAGRAM_DM") {
      displayToast('error', 'No Response Created')
      valid = false
      return valid
    }

    if (responseData?.responseData?.type == "text") {
      `
        Check Text
        Check Button Text -> Title, URL
      `
      if (responseData?.responseData?.data.text.length == 0) {
        displayToast('error', 'Enter text message in response')
        valid = false
      };

      if (responseData?.responseData?.data.text.length > 1000) {
        displayToast('error', 'Text message in response is greater than 1000')
        valid = false
      };


      const btn = responseData?.responseData?.data?.buttonText || []
      if (btn.length > 0) {
        for (let i = 0; i < btn.length; i++) {
          const curr_btn: any = btn[i]
          if (curr_btn?.title.length > 20 || curr_btn?.title.length == 0) {
            displayToast('error', `Button ${i + 1} title is invalid`)
            valid = false
          };

          if (curr_btn?.type == "link" && (curr_btn?.data.length == 0 || !validateUrl(curr_btn?.data))) {
            displayToast('error', `Button ${i + 1} URL is invalid`)
            valid = false
          };
        }
      }


    }


    if (responseData?.responseData?.type == "card") {
      `
        Check Each Card for:
        - Image
        - Title
        - Subtitle
        - Buttons

      `
      const cards: any = responseData?.responseData?.data || []

      for (let j = 0; j < cards.length; j++) {
        const card = cards[j];

        if (card.title.length == 0) {
          displayToast('error', `Card ${j + 1} has no title`)
          valid = false
        };

        if (card.title.length > 80) {
          displayToast('error', `Card ${j + 1} Title in too long`)
          valid = false
        };

        if (card.subtitle.length == 0) {
          displayToast('error', `Card ${j + 1} has no subtitle`)
          valid = false
        };

        if (card.subtitle.length > 80) {
          displayToast('error', `Card ${j + 1} subtitle in too long`)
          valid = false
        };

        if (card?.image_url.length == 0 || !validateUrl(card?.image_url)) {
          displayToast('error', `Card ${j + 1} Image Url is invalid`)
          valid = false
        };

        const btn = card?.buttons || []
        if (btn.length > 0) {
          for (let i = 0; i < btn.length; i++) {
            const curr_btn: any = btn[i]
            if (curr_btn?.title.length > 20 || curr_btn?.title.length == 0) {
              displayToast('error', `Card ${i + 1} Button ${i + 1} title is invalid`)
              valid = false
            };
            if (curr_btn?.data.length == 0 || !validateUrl(curr_btn?.data)) {
              displayToast('error', `Card ${i + 1} Button ${i + 1} URL is invalid`)
              valid = false
            };
          }
        }

      }




    }

    return valid
  }


  return (
    <div className="w-full">
      {/* Top Navigation Bar */}
      <div className="z-10 m-2 md:m-4 border-b rounded-lg border-gray-200 sticky top-0 bg-white">
        <div className="mx-auto px-2 md:px-4 py-3 md:h-16 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-0">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm md:text-base"><span className='hover:cursor-pointer hover:text-blue-500' onClick={() => navigate('/dashboard/automations')}>Automation</span> /</span>
            {isNameEditing ? (
              <form onSubmit={handleNameSave} className="inline-flex items-center flex-1">
                <input
                  type="text"
                  value={automationName}
                  onChange={(e) => setAutomationName(e.target.value)}
                  className="border-b border-gray-300 focus:border-purple-500 outline-none px-1 py-0.5 text-gray-800 min-w-[120px] md:min-w-[150px] text-sm md:text-base"
                  autoFocus
                />
                <button type="submit" className="ml-2 text-gray-500 hover:text-gray-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </form>
            ) : (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-gray-800 font-medium text-sm md:text-base truncate">{automationName}</span>
                <button onClick={handleNameEdit} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between md:justify-end w-full md:w-auto gap-2 md:gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={isEnabled}
                onCheckedChange={handleSwitchChange}
                className="data-[state=checked]:bg-green-500"
              // disabled={hasUnsavedChanges()}
              />
              <label className={"font-medium text-sm md:text-base " + (isEnabled ? 'text-green-600' : 'text-gray-400')}>
                {isEnabled ? 'Active' : 'Inactive'}
              </label>
            </div>
            <button
              onClick={handleSaveAutomation}
              className="px-3 md:px-4 py-1.5 md:py-2 bg-[#3535d5] text-white rounded-lg hover:bg-[#7373ff] transition-colors text-sm md:text-base"
            >
              Save Automation
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-[600px] mx-auto p-2 space-y-6">
        {triggerData && <TriggerStep data={triggerData} onUpdate={setTriggerData} />}
      </div>

      <div className="max-w-full mx-auto p-4 px-0 space-y-6">
        {responseData && <ResponseStep data={responseData} triggerData={triggerData} onUpdate={setResponseData} automationId={automationId} />}

      </div>
    </div>
  );
};

export default AutomationBuilderV0;